'use client'

import ReactMarkdown from 'react-markdown'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@mdxui/primitives'
import { Markdown } from '../../lib/markdown'
import { mapFAQProps } from './mappers'

/**
 * FAQ item structure
 */
export interface FAQItem {
  question: string
  answer: string
}

/**
 * Adapted FAQ props that makes variant optional since this component
 * only implements the accordion variant. Following the Hero adapter pattern.
 */
export interface AdaptedFAQProps {
  /**
   * Section title (uses 'title' from FAQProps)
   */
  title?: string
  /**
   * Optional description text below the title
   */
  description?: string
  /**
   * FAQ items - array of question/answer pairs
   */
  items: FAQItem[]
  /**
   * FAQ variant (optional, defaults to 'accordion')
   * This component only implements accordion style.
   * For other variants, use FAQNumberedGrid (numbered-grid) or FAQSplitCTA (split-cta).
   */
  variant?: 'accordion' | 'grid' | 'list'
}

/**
 * FAQ - Frequently Asked Questions section
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Accordion-style expandable questions and answers
 * - Answers support **Markdown** (bold, lists, inline code, links, paragraphs)
 * - Centered title with optional description
 * - 4-8 items recommended for balanced layout
 *
 * **Markdown Support in Answers:**
 * - `**bold**` for emphasis
 * - `\`inline code\`` for code snippets
 * - `- item` for bullet lists
 * - `[text](url)` for links
 * - `\n\n` for paragraph breaks
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────┐
 * │    Frequently Asked Questions      │
 * │    Optional description text       │
 * │                                    │
 * │  ▸ Question 1?                     │
 * │  ▸ Question 2?                     │
 * │  ▾ Question 3?                     │
 * │    Answer with **Markdown**...     │
 * │  ▸ Question 4?                     │
 * └────────────────────────────────────┘
 * ```
 *
 * @example
 * ```tsx
 * <FAQ
 *   title="Frequently Asked Questions"
 *   description="Find answers to common questions."
 *   items={[
 *     {
 *       question: 'What is mdxui?',
 *       answer: 'A **type-safe** component library for MDX.'
 *     },
 *     {
 *       question: 'How do I install?',
 *       answer: 'Run `pnpm add mdxui @mdxui/primitives`.'
 *     },
 *   ]}
 * />
 * ```
 */
export function FAQ(props: AdaptedFAQProps) {
  const { heading, description, items } = mapFAQProps(props)

  return (
    <section className="relative bg-background py-16 lg:py-32">
      <div className="container max-w-3xl mx-auto">
        <div className="mb-8 text-center lg:mb-12">
          <h2 className="text-3xl font-semibold lg:text-4xl">{heading}</h2>
          {description && (
            <div className="mt-4">
              <Markdown size="base">{description}</Markdown>
            </div>
          )}
        </div>
        <Accordion type="single" collapsible>
          {items.map((item, index) => (
            <AccordionItem key={item.id} value={`item-${index}`}>
              <AccordionTrigger className="font-semibold hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent>
                <div className="prose prose-sm max-w-none text-muted-foreground prose-headings:text-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-pre:bg-muted prose-pre:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{item.answer}</ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  )
}

export default FAQ
